import React from 'react';
import Reveal from './Reveal';
import { FileText, Users2, Brain, ShieldCheck, Send, ChevronRight } from 'lucide-react';

const stages = [
  {
    icon: FileText,
    tag: 'Step 1',
    title: 'Client brief',
    body: 'Spec, targeting, volume — configured in minutes.',
    color: 'from-brand-blue to-brand-blue-light',
  },
  {
    icon: Users2,
    tag: 'Step 2',
    title: 'Audience match',
    body: 'Your own DB plus 165M+ B2B professionals we can reach.',
    color: 'from-brand-blue to-brand-blue-light',
  },
  {
    icon: Brain,
    tag: 'Step 3',
    title: 'Enrich IQ scoring',
    body: 'Dual scores per lead: opportunity + personalised fit.',
    color: 'from-brand-pink to-brand-pink/70',
    accent: true,
  },
  {
    icon: ShieldCheck,
    tag: 'Step 4',
    title: 'Multi-layer verify',
    body: 'Directory match, deliverability, spec check. Auto-replace on fail.',
    color: 'from-brand-blue to-brand-blue-light',
  },
  {
    icon: Send,
    tag: 'Step 5',
    title: 'Delivered under your brand',
    body: 'Straight into your client\u2019s CRM. Your logo on the report.',
    color: 'from-brand-blue to-brand-blue-light',
  },
];

const LeadJourney = () => {
  return (
    <section className="relative bg-white py-20 md:py-24 overflow-hidden">
      {/* Relevant background — abstract data flow / pipeline */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(255,255,255,0.90) 0%, rgba(241,245,251,0.88) 100%), url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      />
      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-brand-blue text-[12px] font-bold tracking-[0.16em] uppercase mb-4">
            The lead journey
          </p>
          <h2 className="text-navy font-black tracking-[-0.01em] leading-[1.12] text-[32px] sm:text-[40px] md:text-[46px]">
            Every lead moves through five stages before your client sees it
          </h2>
          <p className="mt-6 text-body-muted text-[16px] leading-[1.7]">
            From advertiser brief to delivered under your brand — an automated pipeline built on Enrich IQ. No spreadsheets, no chase-ups, no missed SLAs.
          </p>
        </div>

        {/* Desktop pipeline */}
        <div className="hidden lg:block mt-16">
          <div className="relative">
            {/* Flow line behind icons */}
            <div className="absolute top-[38px] left-[8%] right-[8%] h-px">
              <div className="w-full h-px border-t-2 border-dashed border-brand-blue/30" />
              <div className="absolute -top-[3px] left-0 h-2 w-2 rounded-full bg-brand-blue shadow-mk-blue flow-dot" />
            </div>

            <div className="relative grid grid-cols-5 gap-4">
              {stages.map((s, i) => {
                const Icon = s.icon;
                return (
                  <Reveal key={s.title} delay={i * 130} className="text-center">
                    <div className="flex justify-center">
                      <div
                        className={`w-[76px] h-[76px] rounded-full bg-gradient-to-br ${s.color} text-white flex items-center justify-center shadow-mk-blue ring-4 ring-white`}
                      >
                        <Icon size={26} strokeWidth={2.2} />
                      </div>
                    </div>
                    <p className="mt-4 text-brand-blue text-[11px] font-black tracking-[0.14em] uppercase">
                      {s.tag}
                    </p>
                    <h3 className="mt-2 text-navy text-[16px] font-bold leading-tight">{s.title}</h3>
                    <p className="mt-2 text-body-muted text-[13px] leading-[1.6]">{s.body}</p>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tablet + Mobile — vertical flow */}
        <div className="lg:hidden mt-14 relative pl-12 sm:pl-16">
          <div className="absolute left-[22px] sm:left-[30px] top-6 bottom-6 w-px border-l-2 border-dashed border-brand-blue/30" />
          <div className="space-y-6">
            {stages.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 120} className="relative">
                  <div className="absolute -left-12 sm:-left-16 top-1">
                    <div
                      className={`w-[46px] h-[46px] sm:w-[60px] sm:h-[60px] rounded-full bg-gradient-to-br ${s.color} text-white flex items-center justify-center shadow-mk-blue ring-4 ring-white`}
                    >
                      <Icon size={20} strokeWidth={2.2} />
                    </div>
                  </div>
                  <div className="bg-white rounded-2xl shadow-mk p-5">
                    <p className="text-brand-blue text-[11px] font-black tracking-[0.14em] uppercase">
                      {s.tag}
                    </p>
                    <h3 className="mt-2 text-navy text-[17px] font-bold">{s.title}</h3>
                    <p className="mt-2 text-body-muted text-[13.5px] leading-[1.6]">{s.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* Under-the-line chip row */}
        <Reveal className="mt-14 rounded-2xl bg-brand-soft p-5 sm:p-6 flex flex-wrap items-center justify-center gap-3 text-center">
          <span className="inline-flex items-center gap-2 text-navy text-[13.5px] font-bold">
            Advertiser brief
          </span>
          <ChevronRight size={14} className="text-body-muted" />
          <span className="inline-flex items-center gap-2 text-navy text-[13.5px] font-bold">
            Audience match
          </span>
          <ChevronRight size={14} className="text-body-muted" />
          <span className="inline-flex items-center gap-2 text-brand-pink text-[13.5px] font-black">
            Enrich IQ scoring
          </span>
          <ChevronRight size={14} className="text-body-muted" />
          <span className="inline-flex items-center gap-2 text-navy text-[13.5px] font-bold">
            Verify & suppress
          </span>
          <ChevronRight size={14} className="text-body-muted" />
          <span className="inline-flex items-center gap-2 text-navy text-[13.5px] font-bold">
            Delivered under your brand
          </span>
        </Reveal>
      </div>
    </section>
  );
};

export default LeadJourney;
